import React, { useEffect, useState } from "react";
import { useApp } from "../contexts/AppContext";

const pad = (n) => String(n).padStart(2, "0");
const calc = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const d = Math.max(0, t - Date.now());
  return { days: Math.floor(d/86400000), hours: Math.floor((d%86400000)/3600000),
    minutes: Math.floor((d%3600000)/60000), seconds: Math.floor((d%60000)/1000), finished: d===0 };
};

// Coherent scale: cell padding + numeric font + label font scale together
const SIZES = {
  xxxs: { pad: "px-1.5 py-1", num: "text-xs", label: "text-[8px]",  gap: "gap-1",   sep: "text-xs",  outer: "px-2 py-1.5", title: "text-[10px]" },
  xxs:  { pad: "px-2 py-1.5", num: "text-sm", label: "text-[9px]",  gap: "gap-1.5", sep: "text-sm",  outer: "px-3 py-2",   title: "text-xs" },
  xs:   { pad: "px-2.5 py-2", num: "text-lg", label: "text-[10px]", gap: "gap-2",   sep: "text-lg",  outer: "px-4 py-3",   title: "text-sm" },
  sm:   { pad: "px-3 py-2.5", num: "text-2xl",label: "text-xs",     gap: "gap-2",   sep: "text-2xl", outer: "px-5 py-4",   title: "text-base" },
  md:   { pad: "px-4 py-3",   num: "text-4xl",label: "text-sm",     gap: "gap-3",   sep: "text-4xl", outer: "px-6 py-5",   title: "text-lg" },
  lg:   { pad: "px-5 py-4",   num: "text-5xl",label: "text-base",   gap: "gap-4",   sep: "text-5xl", outer: "px-7 py-6",   title: "text-xl" },
  xl:   { pad: "px-6 py-5",   num: "text-6xl",label: "text-lg",     gap: "gap-5",   sep: "text-6xl", outer: "px-8 py-7",   title: "text-2xl" },
};
const FONTS = { fredoka: "font-fredoka", outfit: "font-outfit", nunito: "font-nunito", manrope: "font-manrope" };
// Perfectly round white box per selected widget size
const CIRCLE = {
  xxxs: "w-24 h-24", xxs: "w-28 h-28", xs: "w-32 h-32", sm: "w-40 h-40",
  md: "w-48 h-48", lg: "w-56 h-56", xl: "w-64 h-64",
};

export default function Countdown({ previewSettings, previewLang, forceShow } = {}) {
  const ctx = useApp();
  const settings = previewSettings || ctx.settings;
  const lang = previewLang || ctx.lang;
  const [now, setNow] = useState(calc(settings.countdown_target_iso));
  useEffect(() => {
    setNow(calc(settings.countdown_target_iso));
    const id = setInterval(() => setNow(calc(settings.countdown_target_iso)), 1000);
    return () => clearInterval(id);
  }, [settings.countdown_target_iso]);

  if ((!settings.countdown_enabled && !forceShow) || !now) return null;
  const sz = SIZES[settings.countdown_size] || SIZES.md;
  const font = FONTS[settings.countdown_font] || FONTS.fredoka;
  const numColor = settings.countdown_number_color || "#FFD93D";
  const numBg = settings.countdown_number_bg || "#0B1D3A";
  const labelColor = settings.countdown_label_color || "#FF6B6B";
  const daysLabelColor = settings.countdown_days_label_color || labelColor;
  const showLabel = settings.countdown_label_enabled !== false;
  const label = lang === "en" ? (settings.countdown_label_en || "Countdown") : (settings.countdown_label_pt || "Contagem decrescente");
  const daysLabel = lang === "en"
    ? (settings.countdown_days_label_en || "Days")
    : (settings.countdown_days_label_pt || "Dias");

  // Per-element font family + exact px size (0/empty => inherit scale defaults)
  const numFont = FONTS[settings.countdown_number_font] || font;
  const numPx = Number(settings.countdown_number_size_px) || 0;
  const numSizeCls = numPx > 0 ? "" : sz.num;
  const numSizeStyle = numPx > 0 ? { fontSize: `${numPx}px` } : {};

  const dlFont = FONTS[settings.countdown_days_label_font] || font;
  const dlPx = Number(settings.countdown_days_label_size_px) || 0;
  const dlSizeCls = dlPx > 0 ? "" : sz.label;
  const dlSizeStyle = dlPx > 0 ? { fontSize: `${dlPx}px` } : {};

  const msgFont = FONTS[settings.countdown_message_font] || font;
  const msgPx = Number(settings.countdown_message_size_px) || 0;
  const msgSizeCls = msgPx > 0 ? "" : sz.title;
  const msgSizeStyle = msgPx > 0 ? { fontSize: `${msgPx}px` } : {};

  // Finished message (editable, custom font/size)
  if (now.finished) {
    const finishedMsg = lang === "en"
      ? (settings.countdown_finished_msg_en || "Contest day! 🎄")
      : (settings.countdown_finished_msg_pt || "Dia do concurso! 🎄");
    return (
      <div className={`inline-flex items-center ${sz.outer} rounded-full bg-[#FF6B6B] text-white ${msgFont} font-bold ${msgSizeCls}`}
           style={msgSizeStyle} data-testid="countdown-finished-msg">
        {finishedMsg}
      </div>
    );
  }

  // Pick the most urgent active reminder (smallest threshold already reached)
  const daysLeft = now.days;
  const activeReminder = [1, 2, 3]
    .map((i) => ({
      enabled: settings[`countdown_reminder${i}_enabled`] === true,
      days: Number(settings[`countdown_reminder${i}_days`]),
      text: lang === "en" ? settings[`countdown_reminder${i}_en`] : settings[`countdown_reminder${i}_pt`],
    }))
    .filter((r) => r.enabled && Number.isFinite(r.days) && r.text && daysLeft <= r.days)
    .sort((a, b) => a.days - b.days)[0];

  const Reminder = () => activeReminder ? (
    <div className={`mt-3 ${sz.outer} rounded-full ${msgFont} font-bold ${msgSizeCls} text-center shadow-md animate-pulse`}
         style={{ background: labelColor, color: "#FFFFFF", ...msgSizeStyle }} data-testid="countdown-reminder-banner">
      🔔 {activeReminder.text}
    </div>
  ) : null;

  const Cell = ({ value, unit }) => (
    <div className="flex flex-col items-center min-w-0" data-testid={`countdown-${unit}`}>
      <div className={`${sz.pad} rounded-xl ${numFont} font-bold ${numSizeCls} leading-none tabular-nums flex items-center justify-center`}
           style={{ background: numBg, color: numColor, minWidth: "2.2em", ...numSizeStyle }}>
        {pad(value)}
      </div>
      <span className={`mt-1 ${unit === "days" ? dlFont : font} font-bold uppercase tracking-wider leading-none ${unit === "days" ? dlSizeCls : sz.label}`}
            style={unit === "days" ? { color: daysLabelColor, ...dlSizeStyle } : { color: "rgba(11,29,58,0.7)" }}>
        {unit === "days" ? daysLabel : unit === "hours" ? (lang === "en" ? "Hrs" : "Hrs")
          : unit === "minutes" ? "Min" : (lang === "en" ? "Sec" : "Seg")}
      </span>
    </div>
  );

  const showDays = settings.countdown_show_days !== false;
  const showHours = settings.countdown_show_hours !== false;
  const showMinutes = settings.countdown_show_minutes !== false;
  const showSeconds = settings.countdown_show_seconds !== false;
  const isDaysOnly = showDays && !showHours && !showMinutes && !showSeconds;
  const circleSize = CIRCLE[settings.countdown_size] || CIRCLE.md;

  const units = [
    { key: "days", value: now.days, show: showDays },
    { key: "hours", value: now.hours, show: showHours },
    { key: "minutes", value: now.minutes, show: showMinutes },
    { key: "seconds", value: now.seconds, show: showSeconds },
  ].filter((u) => u.show);

  if (isDaysOnly) {
    // The WHITE BOX itself becomes a perfect circle; the title sits OUTSIDE (above) it.
    return (
      <div className="inline-flex flex-col items-center" data-testid="countdown-widget">
        {showLabel && <p className={`${font} ${sz.title} font-bold mb-2 text-center leading-tight whitespace-nowrap`} style={{ color: labelColor }}>★ {label} ★</p>}
        <div className={`${circleSize} shrink-0 rounded-full bg-white/90 backdrop-blur-md border-2 shadow-md flex flex-col items-center justify-center`}
             style={{ borderColor: labelColor, aspectRatio: "1 / 1" }} data-testid="countdown-days">
          <div className={`${numFont} font-bold ${numSizeCls} leading-none tabular-nums`} style={{ color: numBg, ...numSizeStyle }}>
            {now.days}
          </div>
          <span className={`mt-1 ${dlFont} font-bold uppercase tracking-wider leading-none ${dlSizeCls}`} style={{ color: daysLabelColor, ...dlSizeStyle }}>
            {daysLabel}
          </span>
        </div>
        <Reminder />
      </div>
    );
  }

  return (
    <div className="inline-flex flex-col items-center" data-testid="countdown-widget">
      <div className={`inline-flex flex-col items-center ${sz.outer} rounded-2xl bg-white/90 backdrop-blur-md border-2 shadow-md`}
           style={{ borderColor: labelColor }}>
        {showLabel && <p className={`${font} ${sz.title} font-bold mb-2 text-center leading-tight whitespace-nowrap`} style={{ color: labelColor }}>★ {label} ★</p>}
        <div className={`flex items-start ${sz.gap}`}>
          {units.map((u, i) => (
            <React.Fragment key={u.key}>
              {i > 0 && (
                <span className={`${font} ${sz.sep} font-bold leading-none pt-1`} style={{ color: numBg, opacity: 0.4 }}>:</span>
              )}
              <Cell value={u.value} unit={u.key} />
            </React.Fragment>
          ))}
        </div>
      </div>
      <Reminder />
    </div>
  );
}
